import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Clock, CheckCircle, FileText, TrendingUp, Download } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useStore } from '../store/useStore';
import { getConfidenceLevel } from '../types';
import CaseListTable from './CaseListTable';
import PullCasesModal from './PullCasesModal';
import { apiService, ProcessingStatus } from '../services/api';
import { API_CONFIG } from '../config/api';
import logger from '../utils/logger';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { cases, isLoading, fetchCases } = useStore();
  const [isPullModalOpen, setIsPullModalOpen] = useState(false);
  const [isPulling, setIsPulling] = useState(false);
  const [modalKey, setModalKey] = useState(0); // Force new modal instance each time

  // Smart Polling State
  const [processingCases, setProcessingCases] = useState<ProcessingStatus[]>([]);
  const [pollingInterval, setPollingInterval] = useState(5000); // Start at 5 seconds
  const pollingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pollingAttemptsRef = useRef(0);

  useEffect(() => {
    if (user?.name) {
      fetchCases();
    }
  }, [user?.name, fetchCases]);

  // Smart Polling Function with Exponential Backoff
  const pollProcessingStatus = useCallback(async () => {
    if (!user?.name) return;

    try {
      logger.debug('Polling for processing cases');
      const processingStatuses = await apiService.getProcessingCases(user.name);

      setProcessingCases(processingStatuses);

      if (processingStatuses.length === 0) {
        // No cases processing - increase backoff
        pollingAttemptsRef.current += 1;
        const newInterval = Math.min(
          5000 * Math.pow(2, pollingAttemptsRef.current),
          30000
        );
        // Use functional setState to get current value without adding to deps
        setPollingInterval((current) => {
          if (newInterval !== current) {
            logger.debug(
              `No processing cases, increasing interval to ${newInterval / 1000}s`
            );
            return newInterval;
          }
          return current;
        });
      } else {
        // Cases are processing - reset to fast polling
        setPollingInterval((current) => {
          if (current !== 5000) {
            logger.debug('Cases processing detected, resetting to 5s interval');
            pollingAttemptsRef.current = 0;
            return 5000;
          }
          return current;
        });

        // Note: We don't need to refresh the full case list here
        // The processingCases state is enough to update Review button states
        logger.debug('Processing cases polled, updating button states');
      }
    } catch (error) {
      logger.error('Failed to poll processing status', error);
    }
  }, [user?.name]);

  // Initial polling when user changes
  useEffect(() => {
    if (!user?.name) return;

    logger.debug('Starting polling');
    pollProcessingStatus();
  }, [user?.name, pollProcessingStatus]);

  // Interval management - only updates when polling interval changes
  useEffect(() => {
    if (!user?.name) return;

    logger.debug(`Setting up polling interval: ${pollingInterval / 1000}s`);

    // Set up interval with current polling rate
    pollingTimerRef.current = setInterval(pollProcessingStatus, pollingInterval);

    // Cleanup old interval when interval changes or on unmount
    return () => {
      if (pollingTimerRef.current) {
        logger.debug('Cleaning up polling interval');
        clearInterval(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
    };
  }, [pollingInterval, pollProcessingStatus, user?.name]);

  const handlePullCasesClick = () => {
    if (!isPulling) {
      logger.debug('Pull Cases clicked, opening modal');
      setIsPulling(true);
      setIsPullModalOpen(true);
    } else {
      logger.warn('Already pulling cases, ignoring click');
    }
  };

  const handlePullCasesSuccess = () => {
    logger.debug('Pull completed successfully, keeping modal open');

    // DON'T close modal here - let user see results and click Close button
    // Just mark as not pulling anymore
    setIsPulling(false);

    // Reset polling to fast interval since new cases may be processing
    setPollingInterval(5000);
    pollingAttemptsRef.current = 0;
  };

  const handlePullCasesClose = (wasDownloadCompleted?: boolean) => {
    logger.debug('User closing modal');

    setIsPullModalOpen(false);
    setIsPulling(false);
    setModalKey((prev) => prev + 1); // Reset modal for next time

    // Only refresh cases if download actually completed successfully
    if (wasDownloadCompleted) {
      setTimeout(() => {
        if (user?.name) {
          logger.debug('Refreshing cases after download');
          fetchCases();
        }
      }, 100);
    } else {
      logger.debug('No download completed, skipping refresh');
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  // Calculate user-specific stats from assigned cases
  const userStats = useMemo(() => {
    const totalCases = cases.length;
    const pendingCases = cases.filter((c) => c.status === 'Pending').length;
    const completedCases = cases.filter((c) => c.status === 'Completed').length;
    const highConfidenceCases = cases.filter(
      (c) => getConfidenceLevel(c.aiConfidence) === 'high'
    ).length;

    // Calculate average confidence for user's cases
    const avgConfidence =
      cases.length > 0
        ? Math.round(cases.reduce((sum, c) => sum + c.aiConfidence, 0) / cases.length)
        : 0;

    return {
      totalCases,
      pendingCases,
      completedCases,
      highConfidenceCases,
      avgConfidence,
    };
  }, [cases]);

  const StatCard: React.FC<{
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color: string;
  }> = ({ title, value, icon, color }) => (
    <div className="bg-white rounded-lg p-6 shadow-sm border border-neutral-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-neutral-600">{title}</p>
          <p className="text-2xl font-bold text-neutral-900 mt-1">{value}</p>
        </div>
        <div className={`p-3 rounded-lg ${color}`}>{icon}</div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-neutral-50 overflow-auto">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 px-8 py-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">
            {getGreeting()}, {user?.name}
          </h1>
          <p className="text-neutral-600">Payroll Collection Agent Dashboard</p>
        </div>
      </div>

      <div className="p-8 space-y-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="My Cases"
            value={userStats.totalCases}
            icon={<FileText className="w-6 h-6 text-white" />}
            color="bg-secondary"
          />
          <StatCard
            title="Pending Review"
            value={userStats.pendingCases}
            icon={<Clock className="w-6 h-6 text-white" />}
            color="bg-blue-500"
          />
          <StatCard
            title="Completed"
            value={userStats.completedCases}
            icon={<CheckCircle className="w-6 h-6 text-white" />}
            color="bg-green-500"
          />
          <StatCard
            title="Avg Confidence"
            value={`${userStats.avgConfidence}%`}
            icon={<TrendingUp className="w-6 h-6 text-white" />}
            color="bg-primary"
          />
        </div>

        {/* Cases Table */}
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200">
          <div className="p-6 border-b border-neutral-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold text-neutral-900">
                  My Assigned Cases
                </h3>
                <p className="text-sm text-neutral-600 mt-1">
                  {userStats.totalCases} case{userStats.totalCases !== 1 ? 's' : ''}{' '}
                  assigned to you
                  {userStats.highConfidenceCases > 0 && (
                    <span className="ml-2 text-green-600">
                      • {userStats.highConfidenceCases} high confidence
                    </span>
                  )}
                </p>
              </div>
              {API_CONFIG.ENVIRONMENT !== 'production' && (
                <button
                  onClick={handlePullCasesClick}
                  disabled={isPulling}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors font-medium shadow-sm ${
                    isPulling
                      ? 'bg-neutral-300 text-neutral-500 cursor-not-allowed'
                      : 'bg-primary text-white hover:bg-primary-600'
                  }`}
                >
                  <Download className={`w-4 h-4 ${isPulling ? 'animate-spin' : ''}`} />
                  <span>{isPulling ? 'Pulling...' : 'Pull Cases'}</span>
                </button>
              )}
            </div>
          </div>
          <CaseListTable
            cases={cases}
            showPagination={true}
            processingCases={processingCases}
          />
        </div>
      </div>

      <PullCasesModal
        key={modalKey}
        isOpen={isPullModalOpen}
        onClose={handlePullCasesClose}
        onSuccess={handlePullCasesSuccess}
      />
    </div>
  );
};

export default Dashboard;
